import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import axios from "axios";
import { TicketCheck, Send, X, Clock, Building2, User, AlertCircle, Eye, ShieldCheck, Paperclip, EyeOff, CheckCircle2, ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip, Legend,
} from "recharts";
import formatApiError from "../lib/apiError";
import { TICKET_STATUSES as STATUSES, STATUS_META, PRIORITY_META, ROLE_LABEL } from "../lib/ticketStatus";
import { AuthContext } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => ({ withCredentials: true });

const IST = { timeZone: "Asia/Kolkata" };
function fmtDateTime(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", ...IST })
    + " · " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, ...IST });
}
function fmtDate(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", ...IST });
}

function StatusPill({ status }) {
  const m = STATUS_META[status] || { label: status, color: "#475569", bg: "#F1F5F9" };
  return (
    <span className="status-tag" style={{ color: m.color, background: m.bg }}>
      {m.label}
    </span>
  );
}

function PriorityPill({ priority }) {
  const m = PRIORITY_META[priority] || PRIORITY_META.medium;
  return (
    <span className="priority-tag" style={{ color: m.color, background: m.bg }}>
      {m.label}
    </span>
  );
}

function PublishPill({ published }) {
  if (published === true) {
    return (
      <span className="publish-tag publish-tag-on">
        <CheckCircle2 size={12} /> Published
      </span>
    );
  }
  if (published === false) {
    return (
      <span className="publish-tag publish-tag-off">
        <EyeOff size={12} /> Hidden
      </span>
    );
  }
  return (
    <span className="publish-tag publish-tag-pending">
      <Clock size={12} /> Untriaged
    </span>
  );
}

// ── Month-strip helpers (same model as QuickViewPage) ───────────────────────
const STRIP_SIZE = 6;
const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const monthLong  = (d) => d.toLocaleString(undefined, { month: "long", year: "numeric" });
const monthLabel = (d) => d.toLocaleString(undefined, { month: "short" });
const buildStrip = (anchor) => {
  const arr = [];
  for (let i = STRIP_SIZE - 1; i >= 0; i--) arr.push(addMonths(anchor, -i));
  return arr;
};

const STATUS_KEYS = ["created", "in_progress", "closed"];

export default function TicketsPage() {
  const { user } = useContext(AuthContext);
  const isSupport = user?.role === "admin" || user?.role === "support_user";

  const today = useMemo(() => startOfMonth(new Date()), []);
  // Currently selected month (default = current). Anchor is the rightmost
  // month visible in the strip — also defaults to current.
  const [anchor, setAnchor]     = useState(today);
  const [selectedMonth, setSelectedMonth] = useState(today);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [filter, setFilter]   = useState("all");
  const [selected, setSelected] = useState(null);

  // Reply box state lives in modal
  const [reply, setReply] = useState("");
  const [posting, setPosting] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [publishSaving, setPublishSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/tickets`, ax());
      setTickets(data);
    } catch (e) {
      setError(formatApiError(e, "Failed to load tickets."));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Tickets created in the selected month. Everything below — counts, pie
  // chart, listing — operates on this scoped subset.
  const monthYM = ym(selectedMonth);
  const monthScoped = useMemo(() => {
    return tickets.filter((t) => {
      if (!t.created_at) return false;
      const d = new Date(t.created_at);
      if (isNaN(d.getTime())) return false;
      return ym(d) === monthYM;
    });
  }, [tickets, monthYM]);

  const filtered = useMemo(() => {
    if (filter === "all") return monthScoped;
    return monthScoped.filter((t) => t.status === filter);
  }, [monthScoped, filter]);

  // Strip controls
  const strip = buildStrip(anchor);
  const canGoForward = anchor.getTime() < today.getTime();

  // Pie chart data — counts per status within the selected month.
  const pieData = useMemo(() => {
    const counts = { created: 0, in_progress: 0, closed: 0 };
    for (const t of monthScoped) {
      if (counts[t.status] != null) counts[t.status] += 1;
    }
    return STATUS_KEYS.map((k) => ({
      key:   k,
      name:  STATUS_META[k].label,
      value: counts[k],
      color: STATUS_META[k].color,
    }));
  }, [monthScoped]);

  const totalThisMonth = pieData.reduce((s, r) => s + r.value, 0);

  const counts = useMemo(() => {
    const c = { all: monthScoped.length, created: 0, in_progress: 0, closed: 0 };
    for (const t of monthScoped) if (c[t.status] != null) c[t.status]++;
    return c;
  }, [monthScoped]);

  const openTicket = useCallback(async (t) => {
    // Always refetch by id to get latest responses
    try {
      const { data } = await axios.get(`${API}/api/tickets/${t.id}`, ax());
      setSelected(data);
      setReply("");
    } catch {
      setSelected(t);
    }
  }, []);

  // Listen for global "open-triage-modal" events fired by the notifications
  // bell when a new ticket arrives while the support user is logged in.
  useEffect(() => {
    const handler = async (e) => {
      const ticketId = e.detail?.ticket_id;
      if (!ticketId) return;
      try {
        const { data } = await axios.get(`${API}/api/tickets/${ticketId}`, ax());
        setSelected(data);
        setReply("");
        // Ensure it appears in the listing too (refresh in background)
        fetchAll();
      } catch (err) {
        // silent — the modal just won't open
      }
    };
    window.addEventListener("open-triage-modal", handler);
    return () => window.removeEventListener("open-triage-modal", handler);
  }, [fetchAll]);

  const handleStatusChange = async (newStatus) => {
    if (!selected || selected.status === newStatus) return;
    setStatusSaving(true);
    try {
      const { data } = await axios.patch(`${API}/api/tickets/${selected.id}/status`, { status: newStatus }, ax());
      setSelected(data);
      setTickets((prev) => prev.map((t) => (t.id === data.id ? { ...t, status: data.status, updated_at: data.updated_at } : t)));
    } catch (e) {
      alert(formatApiError(e, "Failed to update status"));
    } finally { setStatusSaving(false); }
  };

  const handlePublish = async (publishedValue) => {
    if (!selected || selected.published === publishedValue) return;
    setPublishSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/api/tickets/${selected.id}/publish`,
        { published: publishedValue },
        ax(),
      );
      setSelected(data);
      setTickets((prev) => prev.map((t) => (t.id === data.id ? data : t)));
    } catch (e) {
      alert(formatApiError(e, "Failed to update publish state"));
    } finally { setPublishSaving(false); }
  };

  const handleReply = async (e) => {
    e?.preventDefault();
    if (!reply.trim() || !selected) return;
    setPosting(true);
    try {
      const { data } = await axios.post(`${API}/api/tickets/${selected.id}/responses`, { message: reply.trim() }, ax());
      setSelected(data);
      setReply("");
      setTickets((prev) => prev.map((t) => (t.id === data.id ? data : t)));
    } catch (e) {
      alert(formatApiError(e, "Failed to post reply"));
    } finally { setPosting(false); }
  };

  return (
    <div className="page-wrap">
      <div className="page-header">
        <h1 className="page-title">Tickets</h1>
        <p className="page-sub">
          Issues raised by college teams — viewing <strong>{monthLong(selectedMonth)}</strong>.
        </p>
      </div>

      {/* Status distribution — pie chart for the selected month */}
      <div className="chart-card tickets-pie-card">
        <div className="chart-card-head">
          <div>
            <p className="chart-card-title">Tickets by status</p>
            <p className="chart-card-sub">
              {totalThisMonth === 0
                ? `No tickets created in ${monthLong(selectedMonth)} yet.`
                : `${totalThisMonth} ticket${totalThisMonth === 1 ? "" : "s"} created in ${monthLong(selectedMonth)}.`}
            </p>
          </div>
        </div>
        <div className="tickets-pie-body">
          {totalThisMonth === 0 ? (
            <div className="empty-state tickets-pie-empty">
              <TicketCheck size={28} color="#43E0FF" />
              <p>Nothing to chart for this month.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={95}
                  paddingAngle={2}
                  isAnimationActive
                  label={({ value }) => value || ""}
                >
                  {pieData.map((row) => (
                    <Cell key={row.key} fill={row.color} />
                  ))}
                </Pie>
                <RTooltip
                  formatter={(v, n) => [v, n]}
                  contentStyle={{ borderRadius: 10, border: "1px solid #EEEFF5", fontFamily: "Sofia Sans" }}
                />
                <Legend
                  iconType="circle"
                  iconSize={9}
                  wrapperStyle={{ fontFamily: "Sofia Sans", fontSize: 13 }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Month strip (same widget used in Quick View) */}
      <div className="qv-month-strip" data-testid="tickets-month-strip">
        <button
          className="qv-month-arrow"
          onClick={() => setAnchor((a) => addMonths(a, -1))}
          aria-label="Previous month"
          data-testid="tickets-month-prev"
        >
          <ChevronLeft size={18} />
        </button>

        <div className="qv-month-cards">
          {strip.map((d) => {
            const isSelected = ym(d) === ym(selectedMonth);
            const isCurrent  = ym(d) === ym(today);
            const isLast     = ym(d) === ym(addMonths(today, -1));
            let tag = "";
            if (isCurrent) tag = "Current";
            else if (isLast) tag = "Last month";
            return (
              <button
                key={ym(d)}
                className={`qv-month-card${isSelected ? " selected" : ""}${isCurrent ? " current" : ""}`}
                onClick={() => setSelectedMonth(d)}
                data-testid={`tickets-month-${ym(d)}`}
              >
                {tag && <span className="qv-month-tag">{tag}</span>}
                <span className="qv-month-name">{monthLabel(d)}</span>
                <span className="qv-month-year">{d.getFullYear()}</span>
              </button>
            );
          })}
        </div>

        <button
          className="qv-month-arrow"
          onClick={() => setAnchor((a) => addMonths(a, 1))}
          disabled={!canGoForward}
          aria-label="Next month"
          data-testid="tickets-month-next"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* KPI summary */}
      <div className="kpi-strip">
        <div className="kpi-item">
          <Calendar size={16} />
          <span className="kpi-label">Selected month</span>
          <span className="kpi-value">{monthLong(selectedMonth)}</span>
        </div>
        <div className="kpi-item">
          <span className="kpi-label">Tickets this month</span>
          <span className="kpi-value">{totalThisMonth}</span>
        </div>
      </div>

      {/* Status filter tabs */}
      <div className="filter-tabs" role="tablist">
        {STATUSES.map((s) => (
          <button
            key={s.key}
            className={`filter-tab${filter === s.key ? " active" : ""}`}
            onClick={() => setFilter(s.key)}
            data-testid={`filter-${s.key}`}
          >
            {s.label}
            <span className="filter-count">{counts[s.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {loading ? <div className="table-loading">Loading…</div>
       : error  ? <div className="table-error">{error}</div>
       : (
        <div className="data-card">
          {filtered.length === 0 ? (
            <div className="empty-state">
              <TicketCheck size={32} color="#43E0FF" />
              <p>No tickets in this view.</p>
            </div>
          ) : (
            <table className="data-table" data-testid="tickets-table">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>#</th>
                  <th>Title</th>
                  <th>College</th>
                  <th>Raised By</th>
                  <th style={{ width: 130 }}>Status</th>
                  <th style={{ width: 100 }}>Priority</th>
                  <th style={{ width: 110, textAlign: "center" }}>Published</th>
                  <th style={{ width: 90, textAlign: "center" }}>Replies</th>
                  <th style={{ width: 130 }}>Created</th>
                  <th style={{ width: 100, textAlign: "center" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr
                    key={t.id}
                    className="row-clickable"
                    onClick={() => openTicket(t)}
                    data-testid={`ticket-row-${t.id}`}
                  >
                    <td className="td-num">{t.ticket_no}</td>
                    <td className="td-name">{t.title}</td>
                    <td className="td-desc">{t.college_name || <span className="td-empty">—</span>}</td>
                    <td className="td-desc">
                      <div className="td-person-name">{t.raised_by_name}</div>
                      <div className="td-person-sub">{ROLE_LABEL[t.raised_by_role] || t.raised_by_role}</div>
                    </td>
                    <td><StatusPill status={t.status} /></td>
                    <td><PriorityPill priority={t.priority} /></td>
                    <td style={{ textAlign: "center" }}><PublishPill published={t.published} /></td>
                    <td className="td-num" style={{ textAlign: "center" }}>{t.responses?.length || 0}</td>
                    <td className="td-desc">{fmtDateTime(t.created_at)}</td>
                    <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn-view"
                        onClick={() => openTicket(t)}
                        data-testid={`view-ticket-${t.id}`}
                        aria-label={`View ticket ${t.ticket_no}`}
                      >
                        <Eye size={14} /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal-box modal-box-lg ticket-modal" onClick={(e) => e.stopPropagation()} data-testid="ticket-modal">
            <div className="modal-head">
              <div>
                <p className="ticket-no">{selected.ticket_no}</p>
                <h2 className="modal-title">{selected.title}</h2>
              </div>
              <button className="modal-close" onClick={() => setSelected(null)}><X size={18} /></button>
            </div>

            <div className="ticket-meta-row">
              <span className="ticket-meta-item">
                <Building2 size={13} /> {selected.college_name || "—"}
              </span>
              <span className="ticket-meta-item">
                <User size={13} /> {selected.raised_by_name} <span className="ticket-meta-sub">· {ROLE_LABEL[selected.raised_by_role] || selected.raised_by_role}</span>
              </span>
              <span className="ticket-meta-item">
                <Clock size={13} /> {fmtDateTime(selected.created_at)}
              </span>
              <span className="ticket-meta-item">
                <AlertCircle size={13} /> <PriorityPill priority={selected.priority} />
              </span>
            </div>

            <div className="modal-body modal-body-scroll ticket-body">
              {/* On-behalf-of badge */}
              {selected.on_behalf_of_professor_name && (
                <div className="ticket-onbehalf-block">
                  <ShieldCheck size={14} />
                  <span>
                    Raised on behalf of <strong>{selected.on_behalf_of_professor_name}</strong> (faculty)
                  </span>
                </div>
              )}

              {/* Description */}
              <div className="ticket-desc-block">
                <p className="ticket-desc-label">Description</p>
                <p className="ticket-desc-text">
                  {selected.description || <span className="td-empty">No description provided.</span>}
                </p>
              </div>

              {/* Attached files */}
              {(selected.attachment || selected.permission_document) && (
                <div className="ticket-desc-block">
                  <p className="ticket-desc-label">Files</p>
                  <div className="ticket-files-cell">
                    {selected.attachment && (
                      <a
                        className="ticket-file-link"
                        href={`${API}${selected.attachment.url}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Paperclip size={13} /> {selected.attachment.original_name}
                      </a>
                    )}
                    {selected.permission_document && (
                      <a
                        className="ticket-file-link ticket-file-link-perm"
                        href={`${API}${selected.permission_document.url}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <ShieldCheck size={13} /> {selected.permission_document.original_name}
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Status update */}
              <div className="ticket-status-block">
                <p className="ticket-desc-label">Status Change</p>
                <div className="status-segmented">
                  {STATUSES.filter((s) => s.key !== "all").map((s) => (
                    <button
                      key={s.key}
                      className={`status-seg-btn${selected.status === s.key ? " active" : ""}`}
                      onClick={() => handleStatusChange(s.key)}
                      disabled={statusSaving || !isSupport}
                      style={selected.status === s.key ? { background: s.color, color: "#fff", borderColor: s.color } : {}}
                      data-testid={`set-status-${s.key}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Publish decision */}
              {isSupport && (
                <div className="ticket-status-block">
                  <p className="ticket-desc-label">
                    Do you want this ticket to be published?{" "}
                    <PublishPill published={selected.published} />
                  </p>
                  <div className="publish-segmented">
                    <button
                      className={`publish-seg-btn publish-seg-yes${selected.published === true ? " active" : ""}`}
                      onClick={() => handlePublish(true)}
                      disabled={publishSaving}
                      data-testid="publish-yes"
                    >
                      <CheckCircle2 size={14} /> Yes — publish
                    </button>
                    <button
                      className={`publish-seg-btn publish-seg-no${selected.published === false ? " active" : ""}`}
                      onClick={() => handlePublish(false)}
                      disabled={publishSaving}
                      data-testid="publish-no"
                    >
                      <EyeOff size={14} /> No — keep hidden
                    </button>
                  </div>
                  <p className="ticket-publish-hint">
                    Unpublished tickets are visible only to Super Admin and Support User.
                  </p>
                </div>
              )}

              {/* Responses thread */}
              <div className="ticket-thread">
                <p className="ticket-desc-label">Conversation ({selected.responses?.length || 0})</p>
                {selected.responses?.length === 0 ? (
                  <p className="td-empty" style={{ margin: "8px 0" }}>No replies yet.</p>
                ) : (
                  selected.responses.map((r) => {
                    const isSupport = r.author_role === "admin";
                    return (
                      <div key={r.id} className={`reply ${isSupport ? "reply-admin" : "reply-user"}`} data-testid={`reply-${r.id}`}>
                        <div className="reply-head">
                          <span className="reply-author">{r.author_name}</span>
                          <span className="reply-role">{ROLE_LABEL[r.author_role] || r.author_role}</span>
                          <span className="reply-time">{fmtDateTime(r.created_at)}</span>
                        </div>
                        <p className="reply-msg">{r.message}</p>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Reply box */}
            <form className="reply-form" onSubmit={handleReply}>
              <textarea
                className="field-input field-textarea reply-input"
                placeholder="Write a reply…"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={2}
                maxLength={4000}
                data-testid="reply-input"
              />
              <button
                type="submit"
                className="btn-primary"
                disabled={posting || !reply.trim()}
                data-testid="send-reply-btn"
              >
                {posting ? <span className="btn-spinner" /> : (<><Send size={14} /> Reply</>)}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
